import classNames from "classnames";
import { html, TemplateResult } from "lit-html";
import { useAdminTab } from "./adminTab/useAdminTab";
import { useFlightStatusTab } from "./flightStatusTab/useFlightStatusTab";
import { useReduxState } from "Shared/redux/useReduxState";
import { unsafeHTML } from "lit-html/directives/unsafe-html";
import { useState } from "Shared/haunted/CustomHooks";
import { DynamicSettings } from "Components/admin/DynamicSettings";

export const accordionNames = ["manage-your-booking", "check-in", "flight-status"] as const;
export type AccordionName = (typeof accordionNames)[number];

interface Accordion {
	icon: string;
	label: string;
	name: AccordionName;
	contentTemplate: () => TemplateResult;
}

export interface Props {
	culture: string;
	dynamicSettings: DynamicSettings;
    isUsingAmericanMiles: boolean;
}

export const useMyBookingMobileTab = (props: Props) => {
	const [kontent] = useReduxState("kontent");

	const [openedAccordionName, setOpenedAccordionName] = useState<AccordionName>(undefined);

	const manageBookingTab = useAdminTab({
		culture: props.culture,
		isActive: openedAccordionName === "manage-your-booking",
		type: "manage",
	});

	const checkinTab = useAdminTab({
		culture: props.culture,
		isActive: openedAccordionName === "check-in",
		type: "check-in",
	});

	const flightStatusTab = useFlightStatusTab({
		culture: props.culture,
		dynamicSettings: props.dynamicSettings,
		isActive: openedAccordionName === "flight-status",
		isUsingAmericanMiles: props.isUsingAmericanMiles,

	});

	const accordions: Accordion[] = [
		{
			icon: kontent?.manageYourFlight.manageYourFlightTest.contents.searchboxIcon.value,
			label: kontent?.manageYourFlight.manageYourFlightTest.contents.searchboxTitle.value,
			name: "manage-your-booking",
			contentTemplate: manageBookingTab.htmlTemplate,
		},
		{
			icon: kontent?.checkIn.checkInTest.contents.searchboxIcon.value,
			label: kontent?.checkIn.checkInTest.contents.searchboxTitle.value,
			name: "check-in",
			contentTemplate: checkinTab.htmlTemplate,
		},
		{
			icon: kontent?.flightStatus.flightStatusTest.contents.searchboxIcon.value,
			label: kontent?.flightStatus.flightStatusTest.contents.searchboxTitle.value,
			name: "flight-status",
			contentTemplate: flightStatusTab.htmlTemplate,
		},
	];

	const handleClick = (accordionName: AccordionName) =>
		setOpenedAccordionName(openedAccordionName === accordionName ? undefined : accordionName);

	const accordionHeaderTitleTemplate = (accordion: Accordion) => html`
		<div class="flex items-center gap-2 text-n-blue">
			<span class="text-xl/none">${unsafeHTML(accordion.icon)}</span
			><span class="text-base/none">${unsafeHTML(accordion.label)}</span>
		</div>
	`;

	const accordionsTemplate = () =>
		accordions.map(
			(accordion, index) => html`
				<dc-accordion
					class=${classNames("w-full px-4 py-4", {
						"border-b border-solid border-x-0 border-t-0 border-b-[#E0E0E0]":
							index !== accordions.length - 1,
					})}
					.contentTemplate=${accordion.contentTemplate}
					.headerTitle=${accordionHeaderTitleTemplate(accordion)}
					.isOpen=${openedAccordionName === accordion.name}
					.onClick=${() => handleClick(accordion.name)}
				></dc-accordion>
			`
		);

	const htmlTemplate = () => html` <div class="flex w-full flex-col py-2">${accordionsTemplate()}</div> `;

	return { close: () => setOpenedAccordionName(undefined), htmlTemplate };
};
